import "@livekit/components-styles";
import { useEffect, useMemo, useState } from "react";
import useMedia from "use-media";
import { formatDistanceStrict } from "date-fns";
import { ru } from "date-fns/locale";
import { ControlBar } from "@entities/appointment/ui/control-bar/control-bar";
import { VideoConference } from "@entities/appointment/ui/video-conference/video-conference";
import { FullscreenVideoModal } from "@features/appointment/fullscreen-video/ui/full-screen-modal/full-screen-modal";
import { useFullscreenVideoModal } from "@features/appointment/fullscreen-video/model/useFullscreenVideoModal";
import { useFullscreenLayout } from "@features/appointment/fullscreen-video/model/useFullscreenLayout";
import { useConferenceSettingsModal } from "@features/appointment/conference-settings";
import { useCheckConfModal } from "@features/appointment/check-conf/model/useCheckConfModal";
import { ConnectableState } from "./components/connectable-state";
import { EndedState } from "./components/ended-state";
import { WaitingState } from "./components/waiting-state";
import { usePacketLossSuggestion } from "./use-packet-loss-suggestion";
import s from "./video-conf.module.scss";
import { useAddReviewModal } from "@features/student/add-review/model/useAddReviewModal";
import { Plugins } from "@features/appointment/synchronized-audio/ui/plugins";
import { AppointmentAudioRenderer } from "@features/appointment/synchronized-audio/ui/appointment-audio-renderer/appointment-audio-renderer";

const statusByFlags = ({ isConf, confIsEnd, shouldRequest }) => {
  if (isConf) {
    return "live";
  }
  if (confIsEnd) {
    return "ended";
  }
  if (shouldRequest) {
    return "connectable";
  }
  return "waiting";
};

export const VideoConf = ({
  isConf,
  setIsConf,
  appointmentRecord,
  shouldRequest,
  disabled,
  confIsEnd,
  startsAt,
  appointment,
  isTeacher,
}) => {
  const [hasRecord, setHasRecord] = useState(false);
  const [isRecordVisible, setIsRecordVisible] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const isMobile = useMedia({ maxWidth: "768px" });
  const { isOpen: isFullscreen } = useFullscreenVideoModal();
  const selectLayout = useFullscreenLayout();
  const { open: openSettings } = useConferenceSettingsModal();
  const { open: openCheckConf } = useCheckConfModal();
  const { open: openAddReview } = useAddReviewModal();

  const status = statusByFlags({ isConf, confIsEnd, shouldRequest });

  usePacketLossSuggestion();

  useEffect(() => {
    if (!confIsEnd || !appointmentRecord?.url) {
      setHasRecord(false);
      return;
    }

    setHasRecord(true);
  }, [appointmentRecord, confIsEnd]);

  useEffect(() => {
    if (status !== "waiting") {
      return undefined;
    }

    const timerId = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(timerId);
  }, [status]);

  const dateLabel = useMemo(() => {
    if (!startsAt) {
      return "Дата и время уточняются";
    }

    return new Date(startsAt).toLocaleString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
      weekday: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [startsAt]);

  const countdownLabel = useMemo(() => {
    if (!startsAt) {
      return "Начнется скоро";
    }

    const startDate = new Date(startsAt);
    if (startDate <= now) {
      return "Начинается сейчас";
    }

    return `Начнется через: ${formatDistanceStrict(startDate, now, {
      locale: ru,
      unit: "second",
    })}`;
  }, [now, startsAt]);

  if (status === "live") {
    return (
      <div className={s.wrapper}>
        {!isMobile && <FullscreenVideoModal />}
        {(isFullscreen && !isMobile) || (
          <>
            <VideoConference isFocused={true} />
            <AppointmentAudioRenderer />
            <ControlBar
              isFullscreen={true}
              isTeacher={isTeacher}
              onSelectLayout={(mode) => selectLayout(mode, { isTeacher })}
            />
            {/* На мобильных инструменты аудио перенесены в шторку материалов (MobilePlugins) */}
            {!isMobile && <Plugins />}
          </>
        )}
      </div>
    );
  }

  if (status === "ended") {
    return (
      <EndedState
        dateLabel={dateLabel}
        hasRecord={hasRecord}
        appointmentRecord={appointmentRecord}
        isRecordVisible={isRecordVisible}
        setIsRecordVisible={setIsRecordVisible}
        isTeacher={isTeacher}
				feedback_rating={appointment?.feedback_rating}
				feedback_message={appointment?.feedback_message}
				openAddReview={() => openAddReview({
					appointmentId: appointment?.id,
					review: {
						feedback_rating: appointment?.feedback_rating,
						feedback_message: appointment?.feedback_message,
					},
				})}
      />
    );
  }

  if (status === "connectable") {
    return (
      <ConnectableState
        dateLabel={dateLabel}
        disabled={disabled}
        onConnect={() => setIsConf(true)}
        appointment={appointment}
        isTeacher={isTeacher}
        onOpenSoundTest={openSettings}
        onOpenCameraTest={openCheckConf}
      />
    );
  }

  return (
    <WaitingState
      title="Занятие скоро начнется"
      dateLabel={dateLabel}
      countdownLabel={countdownLabel}
      appointment={appointment}
      isTeacher={isTeacher}
      onOpenSoundTest={openSettings}
      onOpenCameraTest={openCheckConf}
    />
  );
};
